/**
 * Navigation chrome: the sidebar rail, the mobile tab bar, the hamburger
 * drawer and the header.
 *
 * `nav.items.<id>` is keyed by the `id` on each `NavItemDef` in `nav-config.ts`
 * rather than by the English label, so renaming a row in English never
 * orphans its translation. Section labels are keyed by section id for the
 * same reason.
 */
export const nav = {
  sections: {
    find: "Find",
    learn: "Learn",
    "my-space": "My Space",
    community: "Community",
  },
  items: {
    home: "Home",
    browse: "Browse",
    videos: "Videos",
    "random-question": "Roll & Talk",
    "my-topics": "My Topics",
    vocabulary: "My Vocabulary",
    collections: "Collections",
    notes: "Notes",
    progress: "Progress",
    "study-groups": "Study Groups",
    "ai-chat": "AI Chat",
    leaderboard: "Leaderboard",
  },
  groups: {
    myTopics: "Topics & practice",
    vocabulary: "Vocabulary",
    community: "Community",
  },
  actions: {
    expand: "Expand {{name}}",
    collapse: "Collapse {{name}}",
    expandSidebar: "Expand sidebar",
    collapseSidebar: "Collapse sidebar",
  },
  header: {
    search: "Search topics",
    searchPlaceholder: "Search topics...",
    menu: "Open menu",
    home: "Home",
  },
  /**
   * The search palette. Two scopes over one field: topic titles, and how a word
   * is actually pronounced in the video library.
   */
  /**
   * The two search dialogs. Each row of the sidebar owns one of them, so the
   * labels here are also the rows' labels — one vocabulary, two surfaces.
   */
  search: {
    trigger: "Search",
    open: "Search topics and pronunciation",
    topicsRow: "Search topics",
    topicsAria: "Search topics",
    topicsTitle: "Search topics",
    topicsDescription: "Find a topic to practise by title or category.",
    pronunciationRow: "Pronunciation",
    pronunciationAria: "Search how a word is pronounced in videos",
    pronunciationTitle: "Pronunciation",
    pronunciationDescription: "Hear a word said by a real speaker in the video library.",
    topicsPlaceholder: "Search topics...",
    pronunciationPlaceholder: "Find a word in real speech...",
    topicsHint: "Search topics by title or category.",
    topicsHintSecondary: "Every result opens a real lesson you can practise from.",
    topicsNoResultsHint:
      "Topics are matched by title — try a shorter word, or open Browse to filter by category.",
    pronunciationHint: "Hear a word said in a real video.",
    pronunciationHintSecondary:
      "Type a word, then play the exact moment someone says it — slow it down and shadow the line.",
    tryExamples: "Try",
    moments: "Moments",
    tooShort: "Type at least {{count}} characters.",
    topicsError: "Could not search topics right now.",
    pronunciationError: "Could not search the video library right now.",
    topicsEmpty: "No topics match “{{query}}”.",
    pronunciationEmpty: "No video says “{{query}}” yet.",
    footerIdle: "Type to search",
    seeAllTopics: "See all {{count}} results for “{{query}}”",
    searchAllTopics: "Search all topics for “{{query}}”",
    hearInVideos: "Hear it in {{count}} videos",
    searchPronunciation: "Search videos for “{{query}}”",
  },
  avatar: {
    profile: "Profile",
    openProfile: "Open profile settings",
    myTopics: "My Topics",
    myVocabulary: "My Vocabulary",
    takeTour: "Take a tour",
    signOut: "Sign Out",
    signedOutTitle: "Signed out",
    signedOutDescription: "You have been signed out successfully.",
    /** Label over the language + theme group at the foot of the profile menu. */
    device: "This device",
    streak: {
      /** Zero is a state to do, not a number to have. */
      idle: "Start a streak today",
      active: "{{count}} day streak",
      /** The unit alone — the figure beside it is the number. No `count`. */
      dayWord: "day streak",
      openActive: "{{count}} day streak — open streak details",
      openIdle: "No active streak — open streak details",
    },
    level: "Lv. {{level}}",
    levelProgress: "Progress to the next level",
    xp: "{{count}} XP",
    theme: {
      label: "Appearance",
      light: "Light",
      dark: "Dark",
      system: "System",
      lightAria: "Light theme",
      darkAria: "Dark theme",
      systemAria: "System theme",
    },
  },
  mobile: {
    more: "More",
    tabBar: "Main navigation",
  },
  /** Landing-page header links (apps/www shell is separate). */
  landing: {
    about: "About",
    updates: "Updates",
  },
  breadcrumbHome: "Home",
} as const;
